<template>
    <div>
        <v-overlay :value="loadingSalesInvoiceList">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <div v-if="!loadingSalesInvoiceList" class="pa-5">
            <v-row no-gutters>
                <v-col class="col-item">
                    <date-picker
                        v-model="startDate"
                        :max-date="dateToday"
                        :min-date="minDate"
                        :popover="{ placement: 'bottom', visibility: 'click' }"
                    >
                        <template v-slot="{ inputValue, inputEvents }">
                            <v-text-field
                                id="start_date"
                                v-on="inputEvents"
                                :value="inputValue"
                                append-icon="fas fa-calendar"
                                :label="'Start date' | t"
                                hide-details
                                dense
                                outlined
                            />
                        </template>
                    </date-picker>
                </v-col>
                <v-col class="col-item ml-4">
                    <date-picker
                        v-model="endDate"
                        :max-date="dateToday"
                        :min-date="minDate"
                        :popover="{ placement: 'bottom', visibility: 'click' }"
                    >
                        <template v-slot="{ inputValue, inputEvents }">
                            <v-text-field
                                id="end_date"
                                v-on="inputEvents"
                                append-icon="fas fa-calendar"
                                :label="'End date' | t"
                                :value="inputValue"
                                hide-details
                                dense
                                outlined
                            />
                        </template>
                    </date-picker>
                </v-col>
            </v-row>
        </div>
        <v-row>
            <v-col class="col-item">
                <highcharts
                    v-if="!loadingSalesInvoiceList"
                    ref="chart"
                    :options="pieChartOptions"
                    :updateArgs="updateArgs"
                >
                </highcharts>
            </v-col>
            <v-col class="col-item">
                <highcharts
                    v-if="!loadingSalesInvoiceList"
                    ref="chart"
                    :options="lineChartOptions"
                    :updateArgs="updateArgs"
                >
                </highcharts>
            </v-col>
        </v-row>
        <v-card v-if="!loadingSalesInvoiceList" class="col-item" flat style="border-top: 1px solid lightgray">
            <v-data-table
                :headers="tableHeaders"
                :hide-default-footer="true"
                :items="countryNames"
                :items-per-page="100"
                :sort-by="['total_net']"
                :sort-desc="[true]"
                class="product-table"
                dense
            >
                <template v-slot:item="{ item }">
                    <tr
                        :key="'country_' + item.id"
                        :class="{
                            activeTableRow:
                                activeListElement != null && parseInt(item.id) === parseInt(activeListElement)
                        }"
                        @click="handleCountryTableRowClick(item.name)"
                    >
                        <td>{{ item.name }}</td>
                        <td style="text-align: end">{{ item.count | rn(2) }}</td>
                    </tr>
                </template>
            </v-data-table>
        </v-card>
        <v-card
            v-if="activeListElement != null && !loadingSalesInvoiceList"
            class="col-item"
            flat
            style="border-top: 1px solid lightgray"
        >
            <v-data-table
                :footer-props="footerProps"
                :headers="listHeaders"
                :items="invoicesListItems"
                :items-per-page="25"
                class="product-table"
                dense
            >
                <template v-slot:item="{ item }">
                    <tr :key="'list_' + item.invoice_number">
                        <td>{{ item.invoice_number }}</td>
                        <td>{{ item.invoice_date }}</td>
                        <td style="text-align: left">{{ item.client_name }}</td>
                        <td style="text-align: end">{{ item.total_net | rn(2) }}</td>
                    </tr>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script>
import Store from "../../store";
import { mapState } from "vuex";
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import moment from "moment";
import { Chart } from "highcharts-vue";

export default {
    name: "sales-invoices-view",
    components: {
        DatePicker,
        highcharts: Chart
    },
    data() {
        const self = this;
        return {
            tab: 1,
            tableHeaders: [
                {
                    text: this.t("Country"),
                    align: "left",
                    sortable: false,
                    value: "country"
                },
                {
                    text: this.t("Net sum"),
                    align: "end",
                    sortable: false,
                    value: "count"
                }
            ],
            listHeaders: [
                {
                    text: this.t("Invoice number"),
                    align: "left",
                    value: "invoice_number"
                },
                {
                    text: this.t("Invoice date"),
                    align: "left",
                    value: "invoice_date"
                },
                {
                    text: this.t("Client name"),
                    align: "left",
                    value: "client_name"
                },
                {
                    text: this.t("Net sum"),
                    align: "end",
                    value: "total_net"
                }
            ],
            footerProps: { "items-per-page-options": [25, 50, 100], "text-per-page-rows": "Rows kkk" },
            activeListElement: null,
            startDate: null,
            endDate: new Date(),
            dateToday: new Date(),
            minDate: new Date(2019, 0, 1),
            updateArgs: [true, true, { duration: 500 }],
            pieChartOptions: {
                chart: {
                    type: "pie",
                    allowPointSelect: false,
                    cursor: "pointer"
                },
                tooltip: {
                    useHTML: true,
                    formatter: function() {
                        return `<table>${this.point.custom}</table>`;
                    }
                },
                title: {
                    text: this.t("Net sum")
                },
                credits: {
                    enabled: false
                },
                series: [
                    {
                        name: this.t("Net sum"),
                        point: {
                            events: {
                                click: function() {
                                    self.handleCountryTableRowClick(this.name);
                                }
                            }
                        },

                        data: [
                            {
                                name: "Loading",
                                y: 100
                            }
                        ]
                    }
                ]
            },
            lineChartOptions: {
                xAxis: {
                    categories: [
                        this.t("January"),
                        this.t("February"),
                        this.t("March"),
                        this.t("April"),
                        this.t("May"),
                        this.t("June"),
                        this.t("July"),
                        this.t("August"),
                        this.t("September"),
                        this.t("October"),
                        this.t("November"),
                        this.t("December")
                    ]
                },
                yAxis: {
                    title: { text: this.t("Net sum") }
                },
                credits: {
                    enabled: false
                },
                chart: {
                    type: "line"
                },

                title: {
                    text: this.t("Net sum total")
                },
                plotOptions: {
                    series: {
                        events: {
                            legendItemClick: function(e) {
                                e.preventDefault();
                            }
                        }
                    }
                },
                series: []
            },
            countryNames: [],
            countryColors: ["#000000", "#FF0000", "#800000", "#0D802D", "#061180", "#400000", "#9C702F", "#E88B00"]
        };
    },
    async mounted() {
        this.startDate = moment()
            .subtract(6, "months")
            .toDate();
        await Store.dispatch("SalesInvoiceStore/getSalesInvoices", {
            start_date: moment(this.startDate).format("YYYY-MM-DD"),
            end_date: moment(this.endDate).format("YYYY-MM-DD")
        });
    },
    computed: {
        ...mapState("SalesInvoiceStore", ["salesInvoices", "loadingSalesInvoiceList", "invoicesListItems"])
    },
    watch: {
        startDate: function() {
            if (!this.loadingSalesInvoiceList) this.updatePageData();
        },
        endDate: function() {
            this.updatePageData();
        },
        salesInvoices: function() {
            this.callAllNeededFunctions();
        }
    },
    methods: {
        async updatePageData() {
            await Store.dispatch("SalesInvoiceStore/getSalesInvoices", {
                start_date: this.startDate ? moment(this.startDate).format("YYYY-MM-DD") : null,
                end_date: this.endDate ? moment(this.endDate).format("YYYY-MM-DD") : null
            });
        },
        callAllNeededFunctions() {
            this.activeListElement = null;
            this.fetchCountryNames();
            this.getPieChartData();
            this.getLineChartData();
        },
        fetchCountryNames() {
            let uniqueValues = [];
            this.countryNames = [];
            this.salesInvoices.map(e => {
                if (!uniqueValues.includes(e.customer_country) && e.customer_country) {
                    uniqueValues.push(e.customer_country);
                }
            });
            for (let i = 0; i < uniqueValues.length; i++) {
                let data = {
                    id: i,
                    name: uniqueValues[i] ? uniqueValues[i] : null,
                    count: 0
                };
                this.countryNames.push(data);
            }
        },
        getPieChartData() {
            let dataArray = [];
            this.salesInvoices.forEach(element => {
                if (element.customer_country) {
                    let currentCountry = JSON.parse(
                        JSON.stringify(this.countryNames.find(({ name }) => name === element.customer_country))
                    );
                    this.countryNames[currentCountry.id].count += element.total_net;
                }
            });
            this.countryNames.sort(function(a, b) {
                return b.count - a.count;
            });
            let allOthersCombinedString = "";
            for (let i = 0; i < this.countryNames.length; i++) {
                let customTitle = `<tr style="margin: 0px; padding: 0px;"><td><b>${
                    this.countryNames[i].name
                }</b>: </td><td style="text-align: right">${Math.round(this.countryNames[i].count * 100) /
                    100}</td></tr>`;
                if (i < 7) {
                    let param = {
                        name: this.countryNames[i].name,
                        y: Math.round(this.countryNames[i].count * 100) / 100,
                        color: this.countryColors[i],
                        custom: customTitle
                    };
                    dataArray.push(param);
                } else {
                    dataArray[dataArray.length - 1].y += this.countryNames[i].count;
                    allOthersCombinedString += customTitle;
                }
            }
            if (this.countryNames.length > 6) {
                dataArray[dataArray.length - 1].y = Math.round(dataArray[dataArray.length - 1].y);
                dataArray[dataArray.length - 1].name = this.t("Other");
                dataArray[dataArray.length - 1].custom = allOthersCombinedString;
            }
            this.pieChartOptions.series[0].data = dataArray;
        },
        getLineChartData() {
            this.lineChartOptions.series = [];
            let dataArray = [];
            let lineName = "Total";
            if (this.activeListElement != null) {
                let currentCountry = this.countryNames.find(({ id }) => id === this.activeListElement);
                dataArray = this.salesInvoices.filter(invoice => invoice.customer_country === currentCountry.name);
                lineName = currentCountry.name;
            } else {
                dataArray = this.salesInvoices;
                lineName = "Total";
            }
            let lastDate = this.salesInvoices[this.salesInvoices.length - 1].invoice_date;
            let monthArray = [];
            for (let i = 0; i < 12; i++) {
                monthArray.unshift(
                    moment(lastDate, "YYYY-MM-DD")
                        .subtract(i, "month")
                        .format("MMMM YYYY")
                );
            }
            let totalNetSum = new Array(12).fill(0);
            for (let i = 0; i < dataArray.length; i++) {
                let invoiceDate = moment(dataArray[i].invoice_date, "YYYY-MM-DD");
                let index = monthArray.indexOf(invoiceDate.format("MMMM YYYY"));
                if (totalNetSum[index]) {
                    totalNetSum[index] += dataArray[i].total_net;
                } else {
                    totalNetSum[index] += dataArray[i].total_net || 0;
                }
            }
            totalNetSum = totalNetSum.map(totalNet => Math.round(totalNet * 100) / 100);
            let arrayLastIndex = totalNetSum.length - 1;
            let untilFirstRealNumber = true;
            while (untilFirstRealNumber) {
                if (totalNetSum[arrayLastIndex] === 0) {
                    totalNetSum.pop();
                    arrayLastIndex--;
                } else {
                    untilFirstRealNumber = false;
                }
            }
            let arrayFirstIndex = 0;
            untilFirstRealNumber = true;
            while (untilFirstRealNumber) {
                if (totalNetSum[arrayFirstIndex] === 0) {
                    totalNetSum.shift();
                    monthArray.shift();
                } else {
                    untilFirstRealNumber = false;
                }
            }
            let param = {
                name: this.t(lineName),
                data: totalNetSum,
                color: this.countryColors[0]
            };
            monthArray = monthArray.map(name => {
                let dateName = name.split(" ");
                return parseInt(dateName[1]) === new Date().getFullYear()
                    ? this.t(dateName[0])
                    : this.t(dateName[0]) + " " + dateName[1];
            });
            this.lineChartOptions.series.push(param);
            this.lineChartOptions.xAxis.categories = monthArray;
            this.lineChartOptions.title.text = `${this.lineChartOptions.yAxis.title.text} ${this.t(lineName)}`;
        },
        handleCountryTableRowClick(countryName) {
            if (typeof countryName === "number") {
                let currentCountry = JSON.parse(JSON.stringify(this.countryNames.find(({ id }) => id === countryName)));
                countryName = currentCountry.name;
            }
            if (countryName === this.t("Other")) {
                this.activeListElement = null;
            } else {
                let currentCountry = JSON.parse(
                    JSON.stringify(this.countryNames.find(({ name }) => name === countryName))
                );

                if (currentCountry.id === this.activeListElement) {
                    this.activeListElement = null;
                    countryName = null;
                } else {
                    this.activeListElement = currentCountry.id;
                    Store.commit("SalesInvoiceStore/updateInvoicesList", countryName);
                }
            }
            this.getLineChartData();
        }
    }
};
</script>

<style>
.activeTableRow {
    background-color: #f41823;
    color: #fff;
}

.activeTableRow:hover {
    background-color: #e11520 !important;
    color: #fff !important;
}
td {
    height: 25px !important;
}
::v-deep .v-data-table-header th {
    white-space: nowrap;
}
</style>
